import {
  AfterContentInit,
  ChangeDetectionStrategy,
  Component,
  ContentChildren,
  Input,
  OnDestroy,
  QueryList,
} from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { AccordionPanelComponent } from './accordion-panel/accordion-panel.component';

/**
 * Accordion Component
 */
@Component({
  selector: 'atx-accordion',
  template: '<div><ng-content></ng-content></div>',
  styleUrls: ['./accordion.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class AccordionComponent implements AfterContentInit, OnDestroy {
  /**
   * Array of all panels contained in the accordion
   */
  @ContentChildren(AccordionPanelComponent)
  public panels: QueryList<AccordionPanelComponent>;

  /**
   * Input to determinate if other panels should close when
   * a new one opens
   */
  @Input() public closeOthers = false;

  /**
   * Array that holds each panel's id and state
   */
  public panelState: { id: string; state: boolean }[] = [];

  /**
   * Subject that notifies all subscriptions to unsubscribe
   */
  private destroyed = new Subject<boolean>();

  /**
   * Triggers after the content has been rendered
   */
  public ngAfterContentInit() {
    this.intializePanels();
    this.panels.changes.pipe(takeUntil(this.destroyed)).subscribe(() => {
      this.intializePanels();
    });
  }

  /**
   * Gets the initial panel state
   */
  public intializePanels() {
    this.panelState = [];
    this.panels.forEach((panel, index) => {
      this.panelState.push({ id: panel.panelId, state: panel.open });
      panel.toggled
        .pipe(takeUntil(this.destroyed))
        .subscribe((incomingPanel: { id: string; open: boolean }) => {
          this.setPanelState(panel, incomingPanel, index);
          this.syncPanelState(this.panelState);
        });
    });
  }

  /**
   * It sets the new state of the panel and closes all others
   * if this.closeOthers is true
   * @param oldPanel The old panel to be changed
   * @param newPanel The new state of the panel
   * @param index The index of the old panel in the
   * panels array
   */
  public setPanelState(
    oldPanel: AccordionPanelComponent,
    newPanel: { id: string; open: boolean },
    index: number,
  ) {
    this.panelState[index].state = newPanel.open;
    if (this.closeOthers && newPanel.open && !oldPanel.open) {
      this.panelState.forEach((tempPanel, tempIndex) => {
        if (tempPanel.id !== newPanel.id && tempPanel.state) {
          this.panelState[tempIndex].state = false;
        }
      });
    }
  }

  /**
   * It syncs the state of the panels with the view
   * @param tempState An array that holds the state of each panel
   */
  public syncPanelState(tempState: { id: string; state: boolean }[]) {
    this.panels.forEach((panel, index) => {
      if (panel.panelId === tempState[index].id) {
        panel.open = tempState[index].state;
      }
    });
  }

  /**
   * Unsubscribes from all subscriptions on component
   * destruction
   */
  public ngOnDestroy(): void {
    this.destroyed.next(true);
    this.destroyed.complete();
  }
}
